import { ParentSize } from '@visx/responsive'
import moment from 'moment'
import { ReactElement, ReactNode, useMemo, useState } from 'react'
import { InProgressLegend } from 'app/components/ai-agent-voice/InProgressLegend'
import {
  groupControlsForRange,
  inProgressRange,
  resolveGroup,
} from 'app/components/ai-agent-voice/newPatientCallsBuckets'
import Spinner from 'app/components/Spinner'
import DateGroupSelector, { GroupBy } from 'app/components/viz/DateGroupSelector'
import BarStackGraph from 'app/components/viz/graphs/BarStackGraph'
import { type CountsBucket, type DailyCountField, type DailyCounts, bucketDailyCounts } from './voiceAgentOpsMetrics'

/** One stacked series: its legend key, chart hue, and its value from a bucket's summed daily counts. */
export type TrendSeries<F extends string = DailyCountField> = {
  key: string
  color: string
  value: (totals: CountsBucket<F>[`totals`]) => number
}

/**
 * Stacked bars over time for the Ops tab (AI calls, failures). Bars run through today,
 * never past it; when today falls in the last bucket (today, this week, this month) that
 * bar is still filling up, so a dashed divider sets it apart. Same shell as
 * `NewPatientCallsGraph` so the tabs read alike. Pass `series` as a stable constant.
 *
 * Daily counts default to the voice agent's fields; another tab passes its own
 * `emptyTotals` (a stable constant naming every field to sum) with matching `series`.
 */
export function OpsTrendGraph<F extends string = DailyCountField>({
  title,
  badge,
  adornment,
  series,
  daily,
  emptyTotals,
  fromDate,
  toDate,
  isLoading,
}: {
  title: string
  badge: string
  /** Renders after the count chip, as on `SectionTitle`. */
  adornment?: ReactNode
  series: TrendSeries<F>[]
  daily: DailyCounts<F>[]
  emptyTotals?: Record<F, number>
  fromDate: string
  toDate: string
  isLoading: boolean
}): ReactElement {
  const controls = groupControlsForRange(fromDate, toDate)
  const [group, setGroup] = useState<GroupBy>(controls.defaultGroup)
  const effectiveGroup = resolveGroup(group, controls)

  // The viewer's calendar day, not the UTC one: at 9pm US Eastern the UTC day is
  // already tomorrow, which drew an empty bar for a day that has not started here.
  const { lastDay, lastBarInProgress } = inProgressRange(fromDate, toDate, moment().format(`YYYY-MM-DD`))

  const buckets = useMemo(
    () => bucketDailyCounts(daily, fromDate, lastDay, effectiveGroup, emptyTotals),
    [daily, fromDate, lastDay, effectiveGroup, emptyTotals],
  )

  const allKeys = useMemo(() => series.map((s) => s.key), [series])
  const colorMap = useMemo(() => Object.fromEntries(series.map((s) => [s.key, s.color])), [series])

  const data = useMemo(
    () =>
      buckets.map((b, index) => ({
        day: b.label,
        tooltipDisplay: lastBarInProgress && index === buckets.length - 1 ? `${b.tooltip} (in progress)` : b.tooltip,
        values: Object.fromEntries(series.map((s) => [s.key, s.value(b.totals)])),
      })),
    [buckets, series, lastBarInProgress],
  )

  const totals = useMemo(
    () => Object.fromEntries(series.map((s) => [s.key, buckets.reduce((sum, b) => sum + s.value(b.totals), 0)])),
    [buckets, series],
  )

  return (
    <div className="mb-8">
      <div className="mb-3 flex flex-wrap items-center justify-between gap-y-2">
        <div className="flex items-center gap-2">
          <p className="text-ui-lg text-strong">{title}</p>
          <span className="border-default ml-2 flex items-center justify-center rounded border-[0.5px] bg-gray-minimal px-1.5 py-0.5 text-label-xs text-gray-strong">
            {badge}
          </span>
          {adornment}
        </div>
        <DateGroupSelector
          defaultGroup={effectiveGroup}
          disableDays={controls.disableDays}
          disableWeeks={controls.disableWeeks}
          disableMonths={controls.disableMonths}
          onGroupChange={setGroup}
        />
      </div>
      <div className="rounded-lg border border-default p-4">
        <div className="h-[360px]">
          {isLoading ? (
            <div className="flex h-full items-center justify-center">
              <Spinner size={16} />
            </div>
          ) : (
            <ParentSize>
              {({ width, height }) =>
                width && height ? (
                  <BarStackGraph
                    width={width}
                    height={height}
                    data={data}
                    allKeys={allKeys}
                    totals={totals}
                    colorMap={colorMap}
                    integerYAxis
                    lastBarInProgress={lastBarInProgress}
                    rawLegendLabels
                    bottomRightSlot={lastBarInProgress ? <InProgressLegend group={effectiveGroup} /> : undefined}
                  />
                ) : null
              }
            </ParentSize>
          )}
        </div>
      </div>
    </div>
  )
}
